import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getPinball, preloadPinball } from '../pinball-assets.js';
import { genererCanauxRuban, MATCAP_GAIN } from './lib/matcap.js';
import { vdjColor, VdjComfort } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La lumière est celle des photos.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pinball',
  family: 'photographic',
  danceability: 0.68,
  energyLevel: 0.64,
  bpmMin: 110,
  bpmMax: 170,
  movement: 0.60,
  tonality: 0.55,
  enabled: true
};

// PINBALL : un plateau de flipper vu depuis la place du joueur, à partir de
// deux photos identiques (éteint / allumé). Une bille chromée rebondit en
// rythme entre les bumpers, les flippers la renvoient sur les temps, les
// inserts s'allument en chenillards. Électro, pop, rock, funk, big beat.
// Règles de kiosk-app/CLAUDE.md (R1 à R17) ; danceability 0,68 : R9, coupes
// sur la phrase, impulsions de temps modérées.
//
// LES ASSETS viennent de tools/build-pinball.mjs (constantes mesurées dans
// son en-tête) : les deux photos (flippers effacés, zone comblée par
// diffusion), la carte d'identifiants sans perte (R = insert, G = rampe), la
// planche des deux flippers (éteint / allumé, masque en alpha), et le
// manifeste : inserts (type, centroïde, couleur), chaînes de flèches, ordre
// circulaire, bumpers, pivots des flippers, rampe, HOMOGRAPHIE du plateau,
// points de passage en coordonnées du plateau.
//
// LA LUMIÈRE, par pixel : off + (on - off) x intensité(insert) - la
// différence SIGNÉE et non max(on - off, 0) comme le dit le brief : allumés,
// les chapeaux des bumpers passent du plastique blanc à la couleur (des
// canaux baissent), et seule la différence signée redonne pinball-on ;
// l'éclairage général (id 0) a sa propre intensité. Les intensités vivent
// dans une texture de 256 x 1 mise à jour à chaque image (1 Ko). À pleine
// intensité partout, on retrouve pinball-on.jpeg (critère 4).
//
// LES FLIPPERS : leur région (la planche) est tournée autour du pivot dans
// le fragment shader (échantillonnage par rotation inverse) ; sous eux, le
// décor comblé au build - aucun trou quand ils se lèvent. 40° en 40 ms,
// retour en 150 ms.
//
// LA BILLE : une sphère analytique dans le même passage, matcap CHROME (R5,
// lib/matcap.js), rayon donné par l'homographie là où elle est (plus petite
// en haut), ombre de contact ellipse sous elle, et le reflet du plateau
// allumé autour d'elle. Trajectoire SCRIPTÉE dans le plan du plateau : une
// dizaine de parcours d'une mesure, chacun une suite de points de passage
// (flipper, bumpers, slingshots, rampe, couloirs du haut, orbite) aux
// CROCHES de la mesure, reliés par des arcs paraboliques (la pente du
// plateau tire vers le bas) ou des rebonds droits après un bumper ; chaque
// parcours part d'un flipper sur le temps fort et revient à un flipper sur
// le temps fort suivant. La rampe se parcourt en image, la bille un peu
// au-dessus du plateau (ombre décalée).
//
// LE CADRAGE : la pochette DOM ne doit cacher ni les bumpers ni les
// flippers ; la partie visible de l'image est recalculée pour la boîte de la
// pochette courante : les bumpers au-dessus de son bord haut, les flippers
// sous son bord bas (le zoom et la hauteur en découlent).

const NO_BPM = 120;
const FLIP_ANGLE = 40 * Math.PI / 180, FLIP_UP = 0.040, FLIP_DOWN = 0.150;
const G_PLANE = 0.9;               // the table's slope pulls the ball down (plane units/s^2)
const BALLS = 3;
const MULTIBALL_BARS = 4;
const BAND = { bumpers: [140, 252], caps: 212, flippers: [634, 714] };   // image rows to keep clear of the cover (caps: the lit caps' bottom)
const COVER_MARGIN = 0.025;
const SAFE_FLOOR = 0.6;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const FALLBACK = [0xff3bd0, 0x2bffea, 0xffb02b];

// THE ROUTES: [waypoint, eighth of the bar, how the ball gets there]
// 'arc' a parabola on the slope (default), 'line' a straight rebound,
// 'ramp' a ride along the ramp (image space) ending at its exit
export const ROUTES = [
  { name: 'ramp-left', from: 'FL', pts: [['FL', 0], ['RAMP0', 2], ['RAMPX', 6, 'ramp'], ['FL', 8]] },
  { name: 'bumpers', from: 'FL', pts: [['FL', 0], ['B1', 2], ['B12', 3, 'line'], ['B3', 4, 'line'], ['SR', 6], ['FL', 8]] },
  { name: 'orbit', from: 'FL', pts: [['FL', 0], ['ORB', 2], ['ORBT', 3], ['TR', 4], ['B3', 5], ['B2', 6, 'line'], ['SL', 7], ['FR', 8]] },
  { name: 'centre', from: 'FL', pts: [['FL', 0], ['MID', 2], ['B2', 3], ['B1', 4, 'line'], ['SL', 6], ['FR', 8]] },
  { name: 'slings', from: 'FL', pts: [['FL', 0], ['SR', 2], ['SL', 4, 'line'], ['CR', 6], ['FL', 8]] },
  { name: 'ramp-right', from: 'FR', pts: [['FR', 0], ['RAMP0', 2], ['RAMPX', 6, 'ramp'], ['FL', 8]] },
  { name: 'bumpers-r', from: 'FR', pts: [['FR', 0], ['B3', 2], ['B23', 3, 'line'], ['B1', 4, 'line'], ['SL', 6], ['FR', 8]] },
  { name: 'top', from: 'FR', pts: [['FR', 0], ['TOP', 2], ['B2', 3], ['B12', 4, 'line'], ['B1', 5, 'line'], ['SL', 7], ['FR', 8]] },
  { name: 'centre-r', from: 'FR', pts: [['FR', 0], ['MID', 2], ['B2', 3], ['B3', 4, 'line'], ['SR', 6], ['FL', 8]] },
  { name: 'slings-r', from: 'FR', pts: [['FR', 0], ['SL', 2], ['SR', 4, 'line'], ['CL', 6], ['FR', 8]] }
];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function rng(seed) { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
function applyH(H, x, y) { const w = H[6] * x + H[7] * y + H[8]; return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w]; }

// THE CROP for a cover box (safeZone: centre and half sizes in height units,
// y up): the visible image rows [y0, y0 + S] with the bumpers above the
// cover's top and the flippers under its bottom; the columns centred on the
// playfield.
export function cropFor(imgW, imgH, aspect, sc, sh) {
  const top = (1 - (sc.y + sh.y)) / 2, bot = (1 - (sc.y - sh.y)) / 2;   // fractions of the screen, from the top
  const a = top - COVER_MARGIN, b = bot + COVER_MARGIN;
  // the whole bumpers clear of the cover if possible; with a very tall cover
  // (above it, only 13 % of the screen for the 60 % default box), at least
  // their lit caps
  for (const bumpBot of [BAND.bumpers[1], BAND.caps]) {
    const fit = (S) => {
      const lo = Math.max(bumpBot - a * S, 0, BAND.flippers[1] + 8 - S);
      const hi = Math.min(BAND.flippers[0] - b * S, imgH - S, BAND.bumpers[0] - 6);
      return lo <= hi ? (lo + hi) / 2 : null;
    };
    // the largest view (the least zoom) where such a y0 exists: zooming in
    // pulls the bumpers up and the flippers down relative to the cover
    let S = Math.min(imgH, imgW / aspect), y0 = fit(S);
    while (y0 == null && S > BAND.flippers[1] - BAND.bumpers[0] + 12) { S -= 2; y0 = fit(S); }
    if (y0 != null) { const w = S * aspect; return [Math.max(0, Math.min(imgW - w, 700 - w / 2)), y0, w, S]; }
  }
  // nothing fits: the bumpers' caps and the flippers on screen, centred
  const S = Math.min(imgH, imgW / aspect, BAND.flippers[1] - BAND.bumpers[0] + 24), w = S * aspect;
  return [Math.max(0, Math.min(imgW - w, 700 - w / 2)), Math.max(0, BAND.bumpers[0] - 12), w, S];
}

const VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

function fragmentShader() {
  let balls = '', shadows = '', uniforms = '';
  for (let i = 0; i < BALLS; i++) {
    uniforms += `uniform vec4 uBall${i}; uniform vec4 uBallS${i};\n`;
    shadows += `
    if (uBall${i}.w > 0.0) {
      vec2 sd = (img - uBall${i}.xy - uBallS${i}.xy) / (uBall${i}.z * vec2(1.25, 0.7));
      col *= 1.0 - uBallS${i}.z * smoothstep(1.15, 0.45, length(sd)) * uBall${i}.w;
    }`;
    balls += `
    if (uBall${i}.w > 0.0) {
      vec2 d = (img - uBall${i}.xy) / uBall${i}.z;
      float r2 = dot(d, d);
      if (r2 < 1.2) {
        float aa = smoothstep(1.0, 1.0 - 2.0 / uBall${i}.z, sqrt(r2));
        vec3 n = vec3(d.x, -d.y, sqrt(max(1.0 - r2, 0.0)));
        vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
        // the matcap's chrome body (R5); its light boxes are rectangles that
        // read as white bars on a 20 px ball - two soft round reflections instead
        float chrome = m.b * m.b * ${MATCAP_GAIN.toFixed(2)};
        float spec = pow(max(dot(n, normalize(vec3(-0.45, 0.55, 0.70))), 0.0), 28.0) * 1.3
                   + pow(max(dot(n, normalize(vec3(0.5, -0.35, 0.80))), 0.0), 14.0) * 0.25;
        // a mirror: its centre faces the camera and reflects the dark room;
        // toward its rim it reflects the lit playfield around it (sampled
        // outward, farther the closer to the edge); the matcap's soft boxes
        // are the two big light reflections (R5); the edge darkens
        float rr = length(n.xy);
        vec2 dir = rr > 1e-3 ? n.xy / rr : vec2(0.0, -1.0);
        // the playfield around, reflected: four taps across the sampled area
        // (one tap compresses the whole board into the ball: speckle)
        vec2 base = img + vec2(dir.x, -dir.y) * uBall${i}.z * (1.5 + 3.0 * rr);
        vec2 t = vec2(-dir.y, -dir.x) * uBall${i}.z * 0.8;
        vec3 env = 0.25 * (lit(base + t) + lit(base - t) + lit(base + t.yx * vec2(1.0, -1.0)) + lit(base - t.yx * vec2(1.0, -1.0)));
        vec3 bc = mix(vec3(0.035, 0.04, 0.05), env, smoothstep(0.2, 0.9, rr));
        bc += vec3(chrome) * 0.75 + vec3(spec);
        bc *= 1.0 - 0.45 * pow(rr, 6.0);
        col = mix(col, bc, aa * uBall${i}.w);
      }
    }`;
  }
  return `
  precision highp float;
  uniform sampler2D uOff;
  uniform sampler2D uOn;
  uniform sampler2D uIds;
  uniform sampler2D uI;
  uniform sampler2D uFlip;
  uniform sampler2D uMat;
  uniform vec4 uCrop;
  uniform vec2 uImg;
  uniform float uGI;
  uniform vec4 uFlipA;        // left: angle, right: angle, sheet w, sheet h
  uniform vec4 uFlipL;        // pivot x, y, box x, box y
  uniform vec4 uFlipLS;       // box w, h, sheet x, sheet y
  uniform vec4 uFlipR;
  uniform vec4 uFlipRS;
  uniform vec4 uRing0; uniform vec4 uRing1; uniform vec4 uRing2; uniform vec4 uRing3; uniform vec4 uRing4;
  uniform vec4 uRamp;         // colour, amount
  uniform float uShowMask;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  ${uniforms}
  varying vec2 vUv;

  float idAt(vec2 uv) { return floor(texture2D(uIds, uv).r * 255.0 + 0.5); }
  float intensity(float id) { return id < 0.5 ? uGI : texture2D(uI, vec2((id + 0.5) / 256.0, 0.5)).r; }
  float ring(vec4 r, vec2 img) { if (r.w <= 0.0) return 0.0; float d = length((img - r.xy) * vec2(1.0, 1.45)); return r.w * exp(-pow((d - r.z) / 14.0, 2.0)); }
  // the lit playfield (no flippers, no ball) at an image point
  vec3 lit(vec2 img) {
    vec2 uv = vec2(img.x / uImg.x, 1.0 - img.y / uImg.y);
    vec3 o = texture2D(uOff, uv).rgb, n = texture2D(uOn, uv).rgb;
    // the SIGNED difference: a lit bumper cap is coloured where the unlit one
    // is white plastic (some channels darker when lit) - max(on - off, 0)
    // cannot give pinball-on back there; off + (on - off) x k does, at k = 1
    vec3 d = n - o;
    float id = idAt(uv);
    float k = intensity(id) + ring(uRing0, img) + ring(uRing1, img) + ring(uRing2, img) + ring(uRing3, img) + ring(uRing4, img);
    vec3 c = o + d * k;
    float ramp = texture2D(uIds, uv).g;
    // the wires: bright in the photo (linear luminance 0.04..0.2 = sRGB 0.22..0.48)
    if (ramp > 0.0 && uRamp.w > 0.0) c += uRamp.rgb * uRamp.w * ramp * smoothstep(0.04, 0.20, dot(o, vec3(0.2126, 0.7152, 0.0722)));
    return c;
  }
  // a flipper at its angle: its sprite (off + diff x GI), alpha = its mask
  vec4 flipper(vec2 img, float ang, vec4 P, vec4 S) {
    float c = cos(-ang), s = sin(-ang);
    vec2 q = P.xy + mat2(c, s, -s, c) * (img - P.xy);
    vec2 l = q - P.zw;
    if (l.x < 0.0 || l.y < 0.0 || l.x > S.x || l.y > S.y) return vec4(0.0);
    vec2 so = vec2((S.z + l.x) / uFlipA.z, 1.0 - l.y / uFlipA.w);
    vec2 sn = vec2((S.z + l.x) / uFlipA.z, 1.0 - (l.y + uFlipA.w * 0.5) / uFlipA.w);
    vec4 a = texture2D(uFlip, so), b = texture2D(uFlip, sn);
    return vec4(a.rgb + (b.rgb - a.rgb) * uGI, a.a);
  }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    vec2 img = vec2(uCrop.x + vUv.x * uCrop.z, uCrop.y + (1.0 - vUv.y) * uCrop.w);
    vec2 uv = vec2(img.x / uImg.x, 1.0 - img.y / uImg.y);
    vec3 col = lit(img);
    ${shadows}
    vec4 fl = flipper(img, uFlipA.x, uFlipL, uFlipLS);
    col = mix(col, fl.rgb, fl.a);
    vec4 fr = flipper(img, uFlipA.y, uFlipR, uFlipRS);
    col = mix(col, fr.rgb, fr.a);
    ${balls}
    if (uShowMask > 0.5) {
      float id = idAt(uv);
      vec3 o = texture2D(uOff, uv).rgb * 0.4;
      col = id < 0.5 ? o : vec3(fract(id * 0.37), fract(id * 0.61 + 0.3), fract(id * 0.23 + 0.6));
      col += vec3(0.0, 0.6, 0.0) * texture2D(uIds, uv).g;
      col = mix(col, vec3(1.0, 0.5, 0.0), fl.a * 0.6 + fr.a * 0.6);
    }
    gl_FragColor = vec4(col, 1.0);
  }`;
}

let MATCAP_TEX = null;
function matcapTexture() {
  if (MATCAP_TEX) return MATCAP_TEX;
  const m = genererCanauxRuban(256);
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.needsUpdate = true;
  MATCAP_TEX = t;
  return t;
}

export class PinballVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 2;
    this._showMask = urlParam('pinmask') === '1';
    const seg = urlParam('pinball');
    this._pinRoute = seg == null ? null : (ROUTES.findIndex((r) => r.name === seg) >= 0 ? ROUTES.findIndex((r) => r.name === seg) : (Number.isFinite(parseInt(seg, 10)) ? parseInt(seg, 10) % ROUTES.length : null));
    const ins = parseInt(urlParam('pininsert'), 10);
    this._soloInsert = Number.isFinite(ins) ? ins : null;
    this._comfort = new VdjComfort();
    this._post = { bloomStrength: 0.35, bloomThreshold: 0.80, grain: 0, vignette: 0.25, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('pinprobe') === '1' && typeof window !== 'undefined') window.__pinball = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    this._meshes = [];
    this._assets = getPinball();
    if (this._assets) { this._build(this._assets); return; }
    const self = this;
    preloadPinball().then(function (ok) {
      if (!self._scene) return;
      if (ok) { self._assets = getPinball(); self._build(self._assets); }
      // eslint-disable-next-line no-console
      else console.warn('[pinball] assets unavailable');
    });
  }

  _build(A) {
    const M = A.manifest;
    this._M = M;
    this._iData = new Uint8Array(256 * 4);
    this._iTex = new THREE.DataTexture(this._iData, 256, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._iTex.minFilter = this._iTex.magFilter = THREE.NearestFilter; this._iTex.needsUpdate = true;
    const [fl, fr] = M.flippers;
    this._crop = new THREE.Vector4(0, 0, M.size[0], M.size[1]);
    const U = this._U = {
      uOff: { value: A.off }, uOn: { value: A.on }, uIds: { value: A.ids }, uI: { value: this._iTex }, uFlip: { value: A.flippers }, uMat: { value: matcapTexture() },
      uCrop: { value: this._crop }, uImg: { value: new THREE.Vector2(M.size[0], M.size[1]) }, uGI: { value: 0.55 },
      uFlipA: { value: new THREE.Vector4(0, 0, M.sheetSize[0], M.sheetSize[1]) },
      uFlipL: { value: new THREE.Vector4(fl.pivot[0], fl.pivot[1], fl.box[0], fl.box[1]) }, uFlipLS: { value: new THREE.Vector4(fl.box[2], fl.box[3], fl.sheet[0], fl.sheet[1]) },
      uFlipR: { value: new THREE.Vector4(fr.pivot[0], fr.pivot[1], fr.box[0], fr.box[1]) }, uFlipRS: { value: new THREE.Vector4(fr.box[2], fr.box[3], fr.sheet[0], fr.sheet[1]) },
      uRamp: { value: new THREE.Vector4() }, uShowMask: { value: this._showMask ? 1 : 0 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    for (let i = 0; i < 5; i++) U['uRing' + i] = { value: new THREE.Vector4() };
    for (let i = 0; i < BALLS; i++) { U['uBall' + i] = { value: new THREE.Vector4() }; U['uBallS' + i] = { value: new THREE.Vector4() }; }
    this._geo = new THREE.PlaneGeometry(1, 1);
    this._mat = new THREE.ShaderMaterial({ uniforms: U, vertexShader: VERTEX, fragmentShader: fragmentShader(), depthTest: false, depthWrite: false });
    const mesh = new THREE.Mesh(this._geo, this._mat);
    mesh.frustumCulled = false; mesh.renderOrder = -1000;
    const self = this;
    mesh.onBeforeRender = function (renderer) { self._layout(renderer); };
    this._scene.add(mesh);
    this._meshes = [mesh];

    // the waypoints (plane), the ramp (image)
    this._wp = {};
    for (const [k, v] of Object.entries(M.waypoints)) this._wp[k] = v.plane;
    this._rampImg = M.ramp;
    const rx = M.ramp[M.ramp.length - 1];
    const q = applyH(M.inverse, rx[0], rx[1]);
    this._wp.RAMPX = q;
    this._rampLen = []; let acc = 0; this._rampLen.push(0);
    for (let i = 1; i < M.ramp.length; i++) { acc += Math.hypot(M.ramp[i][0] - M.ramp[i - 1][0], M.ramp[i][1] - M.ramp[i - 1][1]); this._rampLen.push(acc); }
    // insert groups
    this._ids = M.inserts.map((i) => i.id);
    this._bumperIds = M.inserts.filter((i) => i.type === 'bumper').map((i) => i.id);
    this._letterIds = M.inserts.filter((i) => i.type === 'letter').map((i) => i.id);
    const r0 = rng(99);
    this._attract = new Float32Array(256); this._phase = new Float32Array(256); this._period = new Float32Array(256);
    for (const id of this._ids) { this._phase[id] = r0() * 6.283; this._period[id] = 2.0 + 2.5 * r0(); }
    this._ev = new Float32Array(256);

    // state
    this._rnd = rng(4242);
    this._time = 0; this._beats = 0; this._eighths = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0;
    this._secondsBeat = 60 / NO_BPM;
    this._flip = [{ t: -1e9 }, { t: -1e9 }];
    this._rings = [];
    this._balls = [];
    this._jackpot = null;
    this._chaserAt = -1e9;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._impacts = [];
    this._paletteKey = null; this._rampCol = new THREE.Color(0.2, 0.9, 1.0);
    this._addBall(0, 'FL', 0);
  }

  _layout(renderer) {
    const rt = renderer.getRenderTarget();
    const size = rt ? _size.set(rt.width, rt.height) : renderer.getDrawingBufferSize(_size);
    const M = this._M;
    // R19: cropped around the centre of composition, not the cover
    const c = cropFor(M.size[0], M.size[1], size.x / Math.max(1, size.y), safeZoneUniforms.uStageCenter.value, safeZoneUniforms.uStageHalfSize.value);
    this._crop.set(c[0], c[1], c[2], c[3]);
  }

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : FALLBACK;
    const key = v.map(String).join('|');
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    this._rampCol = vdjColor(v[0]);
  }

  // A ball: its current route, from bar start `t0` (seconds) at offset
  // `lag` eighths, starting at flipper `from`
  // (a multiball ball waits for the next downbeat: started mid-bar, its past
  // impacts would all fire at once, off the beat)
  _addBall(lag, from, t0, life, deferred) {
    const b = { lag, from, t0: t0 + lag * this._secondsBeat / 2, route: null, kf: null, next: 0, until: life == null ? Infinity : life };
    this._balls.push(b);
    if (!deferred) this._newRoute(b, b.t0);
    return b;
  }
  _newRoute(b, t0) {
    let idx;
    if (this._pinRoute != null) idx = this._pinRoute;
    else {
      const cand = ROUTES.map((r, i) => i).filter((i) => ROUTES[i].from === b.from && i !== b.routeIdx);
      idx = cand[Math.floor(this._rnd() * cand.length)];
    }
    const R = ROUTES[idx];
    b.routeIdx = idx; b.route = R; b.t0 = t0;
    const e = this._secondsBeat / 2;
    // when the route is pinned it may start from the other flipper: start where the ball is
    b.kf = R.pts.map(([w, k, how], i) => ({ w, t: t0 + k * e, how: how || 'arc', p: (i === 0 ? this._wp[b.from] : this._wp[w]) }));
    b.next = 1;
    b.from = R.pts[R.pts.length - 1][0];
    // the flipper strikes the ball as the route starts (the route's last
    // point is the next route's first: it is struck then, not twice)
    b.strike = { w: b.kf[0].w === 'FL' || b.kf[0].w === 'FR' ? (R.pts[0][0] === 'FL' ? 'FL' : 'FR') : null, t: t0 };
  }

  // the ball's position at time t: plane point (on the table) or image point
  // (on the ramp), and whether it is on the ramp
  _ballAt(b, t) {
    const kf = b.kf;
    let i = 1; while (i < kf.length - 1 && t > kf[i].t) i++;
    const a = kf[i - 1], c = kf[i];
    const T = Math.max(1e-3, c.t - a.t), s = Math.max(0, Math.min(T, t - a.t));
    if (c.how === 'ramp') {
      // along the ramp's polyline (image), at constant speed
      const L = this._rampLen[this._rampLen.length - 1], d = L * s / T;
      let j = 1; while (j < this._rampLen.length - 1 && this._rampLen[j] < d) j++;
      const u = (d - this._rampLen[j - 1]) / Math.max(1e-6, this._rampLen[j] - this._rampLen[j - 1]);
      const A = this._rampImg[j - 1], B = this._rampImg[j];
      return { img: [A[0] + (B[0] - A[0]) * u, A[1] + (B[1] - A[1]) * u], ramp: true };
    }
    let p;
    if (c.how === 'line') p = [a.p[0] + (c.p[0] - a.p[0]) * s / T, a.p[1] + (c.p[1] - a.p[1]) * s / T];
    else {
      // a parabola on the slope: p(T) = c, with the pull toward +y
      const vx = (c.p[0] - a.p[0]) / T, vy = (c.p[1] - a.p[1] - 0.5 * G_PLANE * T * T) / T;
      p = [a.p[0] + vx * s, a.p[1] + vy * s + 0.5 * G_PLANE * s * s];
    }
    return { plane: p, ramp: false };
  }

  // image radius of the ball at a plane point (the homography's local scale)
  _radiusAt(p) {
    const H = this._M.homography, r = this._M.ballR;
    const a = applyH(H, p[0] - r, p[1]), b = applyH(H, p[0] + r, p[1]);
    return Math.hypot(b[0] - a[0], b[1] - a[1]) / 2;
  }

  // an impact: what the ball hits lights up / moves
  _impact(w, t, ballIdx) {
    const M = this._M;
    this._impacts.push({ t: +t.toFixed(3), w, beat: +((t - this._barT0) / this._secondsBeat).toFixed(3), ball: ballIdx });
    if (this._impacts.length > 200) this._impacts.shift();
    if (w === 'FL') this._flip[0].t = t;
    else if (w === 'FR') this._flip[1].t = t;
    else if (w[0] === 'B') {
      // the nearest bumper flashes its cap and sends a ring of light around
      const ip = applyH(M.homography, this._wp[w][0], this._wp[w][1]);
      let best = 0, bd = 1e9;
      M.bumpers.forEach((bb, k) => { const d = Math.hypot(bb.base[0] - ip[0], bb.base[1] - ip[1]); if (d < bd) { bd = d; best = k; } });
      this._ev[this._bumperIds[best]] = 1;
      this._rings.push({ x: M.bumpers[best].cx, y: M.bumpers[best].cy + 30, born: t });
    } else if (w === 'SL' || w === 'SR') {
      const s = M.slings[w === 'SL' ? 0 : 1];
      this._rings.push({ x: (s[0][0] + s[1][0] + s[2][0]) / 3, y: (s[0][1] + s[1][1] + s[2][1]) / 3, born: t, small: true });
    } else if (w === 'TOP' || w === 'TL' || w === 'TR') {
      for (const id of this._letterIds) this._ev[id] = 1;
    }
    while (this._rings.length > 5) this._rings.shift();
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  update(deltaTime, profile) {
    if (!this._M) return;
    this._applyPalette(profile.artworkPalette);
    if (profile.playbackState !== 'PLAYING') return;          // R11: a freeze, never black
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const t = this._time += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._secondsBeat = 60 / THREE.MathUtils.clamp(bpm, 60, 200);
    const beatS = this._secondsBeat;
    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= beatS) { this._free -= beatS; beat = true; } }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (this._barT0 == null) this._barT0 = t;

    // MESURE: every ball starts a new route on the downbeat (its own lag
    // kept); the arrows chase toward the ramps
    if (bar) {
      this._barT0 = t;
      this._chaserAt = t;
      for (const b of this._balls) {
        // a ball whose route is not finished yet snaps to its end (a late bar)
        b.pending = t + b.lag * beatS / 2;
      }
    }
    for (const b of this._balls) if (b.pending != null && t >= b.pending) { this._newRoute(b, b.pending); b.pending = null; }
    // PHRASE: multiball, two more balls for 4 bars
    if (phrase && this._balls.length === 1) {
      const life = t + MULTIBALL_BARS * 4 * beatS;
      this._addBall(3, 'FR', this._barT0, life + 4 * beatS, true);
      this._addBall(5, 'FL', this._barT0, life + 4 * beatS, true);
    }
    this._balls = this._balls.filter((b, i) => i === 0 || t < b.until);
    // DROP: jackpot - every insert flashes 4 times, then a circular chase
    let drop = this._tickDrops(profile);
    const director = profile.director;
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && t - this._lastDropAt > 4) { this._lastDropAt = t; this._jackpot = { at: t, flashes: 0 }; }

    // the impacts reached since the last frame
    this._balls.forEach((b, bi) => {
      if (b.strike && b.strike.w && t >= b.strike.t) { this._impact(b.strike.w, b.strike.t, bi); b.strike = null; }
      while (b.kf && b.next < b.kf.length - 1 && t >= b.kf[b.next].t) {
        const k = b.kf[b.next];
        if (k.w !== 'RAMP0' && k.w !== 'RAMPX') this._impact(k.w, k.t, bi);
        b.next++;
      }
    });

    // ---- the lights
    const I = this._iData, ev = this._ev;
    const decay = Math.exp(-dt / 0.22);
    for (const id of this._ids) ev[id] *= decay;
    const eighth = beatS / 2;
    // chasers: on the eighths of the bar after a downbeat
    const ch = Math.floor((t - this._chaserAt) / eighth);
    if (ch >= 0 && ch < 8) for (const c of this._M.chains) { const id = c[ch % c.length]; if (id) ev[id] = Math.max(ev[id], 1); }
    let jack = 0, ringChase = -1;
    if (this._jackpot) {
      const j = (t - this._jackpot.at) / beatS;
      if (j < 4) {
        const k = Math.floor(j);
        if (k >= this._jackpot.flashes) { this._jackpot.flashes = k + 1; this._jackpot.flashOk = this._comfort.requestFlash(t); }
        jack = this._jackpot.flashOk ? Math.exp(-(j - k) * beatS / 0.12) : 0.4;
      } else if (j < 12) ringChase = Math.floor((j - 4) * 4);
      else this._jackpot = null;
    }
    const ring = this._M.ring;
    for (const id of this._ids) {
      const att = 0.12 + 0.30 * (0.5 + 0.5 * Math.sin(t * 6.283 / this._period[id] + this._phase[id]));   // attract mode
      let v = Math.max(att, ev[id], jack);
      if (ringChase >= 0) { const pos = ring.indexOf(id); if (pos >= 0) { const d = ((ringChase - pos) % ring.length + ring.length) % ring.length; v = Math.max(v, Math.exp(-d / 3)); } }
      if (this._soloInsert != null) v = id === this._soloInsert ? 1 : 0;
      I[id * 4] = Math.round(255 * clamp01(v));
    }
    this._iTex.needsUpdate = true;
    this._U.uGI.value = this._soloInsert != null ? 0.3 : Math.max(0.55, jack * 0.9);

    // the rings (bumpers, slings)
    for (let i = 0; i < 5; i++) {
      const r = this._rings[i], u = this._U['uRing' + i].value;
      if (!r) { u.set(0, 0, 0, 0); continue; }
      const a = (t - r.born) / 0.35;
      u.set(r.x, r.y, (r.small ? 20 : 40) + a * (r.small ? 60 : 130), a >= 1 ? 0 : (1 - a) * (r.small ? 0.8 : 1.2));
    }
    this._rings = this._rings.filter((r) => t - r.born < 0.35);

    // the flippers: 40 degrees up in 40 ms, back in 150 ms
    const flipAngle = (f) => { const s = t - f.t; if (s < 0) return 0; if (s < FLIP_UP) return FLIP_ANGLE * s / FLIP_UP; if (s < FLIP_UP + FLIP_DOWN) return FLIP_ANGLE * (1 - (s - FLIP_UP) / FLIP_DOWN); return 0; };
    this._U.uFlipA.value.x = -flipAngle(this._flip[0]);    // left: the tip goes up (image y down)
    this._U.uFlipA.value.y = flipAngle(this._flip[1]);

    // the balls
    let onRamp = false;
    for (let i = 0; i < BALLS; i++) {
      const b = this._balls[i], u = this._U['uBall' + i].value, us = this._U['uBallS' + i].value;
      if (!b || !b.kf) { u.set(0, 0, 1, 0); continue; }
      const at = this._ballAt(b, t);
      let img, r;
      if (at.ramp) {
        img = at.img;
        const q = applyH(this._M.inverse, img[0], img[1]);
        r = this._radiusAt(q) * 1.12;          // a little above the table
        us.set(-r * 0.6, r * 2.2, 0.25, 0);
        onRamp = true;
      } else {
        img = applyH(this._M.homography, at.plane[0], at.plane[1]);
        r = this._radiusAt(at.plane);
        us.set(r * 0.12, r * 0.42, 0.6, 0);
      }
      u.set(img[0], img[1], r, 1);
      b.last = { img, r, ramp: at.ramp };
    }
    const rc = this._rampCol;
    this._rampGlow = (this._rampGlow || 0) + ((onRamp ? 1 : 0) - (this._rampGlow || 0)) * (1 - Math.exp(-dt / 0.08));
    this._U.uRamp.value.set(rc.r, rc.g, rc.b, this._rampGlow * 0.9);
  }

  getProbe() {
    return {
      time: +(this._time || 0).toFixed(3), crop: this._crop ? this._crop.toArray().map((v) => +v.toFixed(1)) : null,
      balls: (this._balls || []).map((b) => ({ route: b.route && b.route.name, at: b.last })), impacts: (this._impacts || []).slice(-24),
      flip: this._U ? [this._U.uFlipA.value.x, this._U.uFlipA.value.y] : null, jackpot: !!this._jackpot
    };
  }

  dispose() {
    for (const m of this._meshes || []) { m.onBeforeRender = null; if (this._scene) this._scene.remove(m); }
    if (this._geo) this._geo.dispose();
    if (this._mat) this._mat.dispose();
    if (this._iTex) this._iTex.dispose();
    if (typeof window !== 'undefined' && window.__pinball === this) window.__pinball = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
